import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { PoliciesLoadComplete } from '@amfam/policy/data-access';
import {
  ANSRequestModel,
  CoverageChangeCustomerTemplateDataModel,
  CoverageChangeNotificationRequestCustomerEmail,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models';
import { fromRouterActions } from '@amfam/shared/utility/navigation';

import * as fromRoot from '../../core/store';
import { ReplaceVehicleService } from '../../core/store/replace-vehicle/replace-vehicle.service';
import {
  CoverageChangeActionTypes,
  CoverageChangeCoveragesLimitsRequestAction,
  CoverageChangeCoveragesLimitsRequestFailAction,
  CoverageChangeCoveragesLimitsRequestSuccessAction,
  CoverageChangeGetQuoteRequestFailAction,
  CoverageChangeGetQuoteRequestSuccessAction,
  CoverageChangeOnlineBindRequestAction,
  CoverageChangeOnlineBindRequestFailAction,
  CoverageChangeOnlineBindRequestSuccessAction,
  CoverageChangeRequestANSNotificationAction,
  CoverageChangeRequestCustomerEmailNotificationAction,
  CoverageChangeRequestCustomerFailEmailNotificationAction,
  CoverageChangeRequestCustomerSuccessEmailNotificationAction,
  CoverageChangeRequestFailANSNotificationAction,
  CoverageChangeRequestNotificationAction,
  CoverageChangeRequestNotificationFailAction,
  CoverageChangeRequestNotificationSuccessAction,
  CoverageChangeRequestSuccessANSNotificationAction,
  CoverageChangesGetQuoteRequestAction,
  SendCoverageAnalyticsAction
} from './coverage-change.actions';

@Injectable()
export class CoverageChangeEffects {
  constructor(
    private rootStore: Store<fromRoot.RootState>,
    private replaceVehicleService: ReplaceVehicleService,
    private action$: Actions
  ) {}

  /**
   * @description: Effect to load the coverage limits for the autopolicies.
   */
  policyCompleteEffect$ = createEffect(() => {
    return this.action$.pipe(
      ofType(PoliciesLoadComplete),
      map(action => action.payload),
      mergeMap(() => [new CoverageChangeCoveragesLimitsRequestAction()]),
      catchError(error => of(new CoverageChangeCoveragesLimitsRequestFailAction(error)))
    );
  });

  /**
   * @description: Effect to load the coverage limits for the autopolicies.
   */
  notificationSplitter$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangeCoveragesLimitsRequestAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_COVERAGES_LIMITS_REQUEST
      ),
      mergeMap(() =>
        this.replaceVehicleService
          .getCoveragesLimits()
          .pipe(map(response => new CoverageChangeCoveragesLimitsRequestSuccessAction(response)))
      ),
      catchError(error => of(new CoverageChangeCoveragesLimitsRequestFailAction(error)))
    )
  );

  getCoverageQuote$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangesGetQuoteRequestAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_QUOTE_REQUEST
      ),
      map(action => action.payload),
      switchMap(payload =>
        this.replaceVehicleService.getCoveragePremiumEstimate(payload).pipe(
          mergeMap(response => [
            new CoverageChangeGetQuoteRequestSuccessAction(response),
            fromRouterActions.Go({
              path: ['/policies/coverage-change/' + payload.vehicleIdentifier + '/review']
            })
          ]),
          catchError(error => of(new CoverageChangeGetQuoteRequestFailAction(error)))
        )
      )
    )
  );

  ansNotification$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangeRequestANSNotificationAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_REQUEST_ANS_NOTIFICATION
      ),
      map(action => action.payload),
      switchMap((payload: ANSRequestModel) =>
        this.replaceVehicleService
          .buildANSNotificationPayload(
            payload.producerId,
            payload.policyNumber,
            payload.notificationText,
            payload.orderNumber,
            payload.notificationType
          )
          .pipe(
            switchMap(refinedPayload =>
              this.replaceVehicleService.ansNotificationForBind(refinedPayload).pipe(
                mergeMap(() => [
                  fromRouterActions.Go({
                    path: ['/policies/coverage-change/change-confirmation']
                  }),
                  new CoverageChangeRequestSuccessANSNotificationAction(payload)
                ]),
                catchError(error => of(new CoverageChangeRequestFailANSNotificationAction(error)))
              )
            )
          )
      )
    )
  );

  emailCustomer$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangeRequestCustomerEmailNotificationAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_REQUEST_EMAIL_NOTIFICATION
      ),
      map(action => action.payload),
      switchMap((payload: CoverageChangeNotificationRequestCustomerEmail) =>
        this.replaceVehicleService
          .generateCustomerEmail(
            payload.email,
            this.replaceVehicleService.buildMessageDetailsForCoverageChangeEmail(payload.message),
            payload.templateId
          )
          .pipe(
            map(response => {
              return new CoverageChangeRequestCustomerSuccessEmailNotificationAction(response);
            }),
            catchError(error =>
              of(new CoverageChangeRequestCustomerFailEmailNotificationAction(error))
            )
          )
      )
    )
  );

  agentEmailNotification$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangeRequestNotificationAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_REQUEST_NOTIFICATION
      ),
      map(action => action.payload),
      switchMap((payload: CoverageChangeCustomerTemplateDataModel) =>
        this.replaceVehicleService.sendAgenEmailForCoverageChange(payload).pipe(
          map(() => {
            return new CoverageChangeRequestNotificationSuccessAction(payload);
          }),
          catchError(error => of(new CoverageChangeRequestNotificationFailAction(error)))
        )
      )
    )
  );

  submitQuoteForBind$ = createEffect(() =>
    this.action$.pipe(
      ofType<CoverageChangeOnlineBindRequestAction>(
        CoverageChangeActionTypes.COVERAGE_CHANGE_ONLINE_BIND_REQUEST
      ),
      map(action => action.payload),
      switchMap((payload: SubmitOnlineBindRequestModel) =>
        this.replaceVehicleService.submitOnlineBind(payload).pipe(
          mergeMap(response => [
            fromRouterActions.Go({
              path: ['/policies/coverage-change/change-confirmation']
            }),
            new CoverageChangeOnlineBindRequestSuccessAction(payload)
          ]),
          catchError(error => of(new CoverageChangeOnlineBindRequestFailAction(error)))
        )
      )
    )
  );

  /**
   * @author: Abhishek Singh
   * @returns:
   * @description: This effect triggers a analytics notification.
   */

  analyticsNotification$ = createEffect(
    () =>
      this.action$.pipe(
        ofType<SendCoverageAnalyticsAction>(CoverageChangeActionTypes.SEND_COVERAGE_ANALYTICS),
        map(action => action.payload),
        map(payload => this.replaceVehicleService.sendCoverageChangeAnalytics(payload))
      ),
    { dispatch: false }
  );
}
